// Adapted from the owned Aieun Studio controller; selection logic is retained.
export function enhanceStudioCatalogue(catalog: HTMLElement) {
  const events = new AbortController();
  const grid = catalog.querySelector<HTMLElement>('.studio-tool-grid')!;
  const cards = [...grid.querySelectorAll<HTMLElement>('.studio-tool-card')];
  const search = catalog.querySelector<HTMLInputElement>('#studio-search')!;
  const clear = catalog.querySelector<HTMLButtonElement>('[data-clear-search]')!;
  const status = catalog.querySelector<HTMLElement>('[data-catalog-status]')!;
  const empty = catalog.querySelector<HTMLElement>('.catalog-empty')!;
  const stage = catalog.querySelector<HTMLElement>('.catalog-stage')!;
  const pagination = catalog.querySelector<HTMLElement>('.catalog-pagination')!;
  const dots = catalog.querySelector<HTMLElement>('.catalog-dots')!;
  const previous = catalog.querySelector<HTMLButtonElement>('[data-page-previous]')!;
  const next = catalog.querySelector<HTMLButtonElement>('[data-page-next]')!;
  const filters = [...catalog.querySelectorAll<HTMLButtonElement>('[data-category]')];
  const normalize = (text: string) => text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const index = cards.map(card => ({ card, text: normalize(card.dataset.search || ''), category: card.dataset.toolCategory }));
  const params = new URLSearchParams(location.search);
  let category = params.get('category') || '';
  if (!filters.some(button => button.dataset.category === category)) category = '';
  search.value = (params.get('q') || '').slice(0, 120);
  let page = Math.max(0, (Number.parseInt(params.get('page') || '1', 10) || 1) - 1);
  const pageSize = () => getComputedStyle(grid).gridTemplateColumns.split(' ').length * 2;
  let size = pageSize();

  const render = () => {
    const terms = normalize(search.value.trim()).split(/\s+/).filter(Boolean);
    const matches = index.filter(item => (!category || item.category === category) && terms.every(term => item.text.includes(term)));
    const count = Math.max(1, Math.ceil(matches.length / size));
    page = Math.min(page, count - 1);
    cards.forEach(card => { card.hidden = true; });
    matches.slice(page * size, (page + 1) * size).forEach(({ card }) => { card.hidden = false; });
    status.textContent = matches.length ? `${matches.length} ${matches.length === 1 ? 'tool' : 'tools'} · Page ${page + 1} of ${count}` : 'No matching tools';
    empty.hidden = matches.length > 0;
    grid.hidden = matches.length === 0;
    const hasMorePages = matches.length > 0 && count > 1;
    pagination.hidden = !hasMorePages;
    stage.classList.toggle('is-paginated', hasMorePages);
    previous.hidden = !hasMorePages;
    next.hidden = !hasMorePages;
    clear.hidden = !search.value;
    filters.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.category === category)));
    previous.disabled = page === 0;
    next.disabled = page === count - 1;
    dots.replaceChildren();
    for (let position = 0; position < count; position++) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.setAttribute('aria-label', `Page ${position + 1} of ${count}`);
      dot.setAttribute('aria-controls', 'studio-tools-grid');
      if (position === page) dot.setAttribute('aria-current', 'page');
      dot.addEventListener('click', () => goTo(position), { signal: events.signal });
      dots.appendChild(dot);
    }
    const url = new URL(location.href);
    for (const name of ['q', 'category', 'page']) url.searchParams.delete(name);
    if (search.value.trim()) url.searchParams.set('q', search.value.trim());
    if (category) url.searchParams.set('category', category);
    if (page) url.searchParams.set('page', String(page + 1));
    history.replaceState(null, '', url);
  };
  const goTo = (target: number) => {
    page = Math.max(0, target);
    render();
    grid.focus({ preventScroll: true });
    grid.scrollIntoView({ block: 'start', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  };
  search.addEventListener('input', () => { page = 0; render(); }, { signal: events.signal });
  catalog.querySelector('form')!.addEventListener('submit', event => { event.preventDefault(); page = 0; render(); }, { signal: events.signal });
  clear.addEventListener('click', () => { search.value = ''; page = 0; render(); search.focus(); }, { signal: events.signal });
  filters.forEach(button => button.addEventListener('click', () => { category = button.dataset.category || ''; page = 0; render(); }, { signal: events.signal }));
  previous.addEventListener('click', () => goTo(page - 1), { signal: events.signal });
  next.addEventListener('click', () => goTo(page + 1), { signal: events.signal });
  const observer = new ResizeObserver(() => {
    // An empty grid has no columns; retain the last valid layout for a cleared search.
    if (grid.hidden) return;
    const nextSize = pageSize();
    if (nextSize !== size) { page = Math.floor(page * size / nextSize); size = nextSize; render(); }
  });
  observer.observe(grid);
  catalog.querySelector<HTMLElement>('.catalog-controls')!.hidden = false;
  render();
  return () => { events.abort(); observer.disconnect(); };
}
